import dayjs from 'dayjs';
import { ReactNode } from 'react';
import type { AgentFinancialResponse } from '../../../api/events-client/events-client.type';
import type {
  AllocationOfCostsChartResponseByCategory,
  CostByInitiativeResponse,
  CostVsBudgetChartResponse,
} from '../../../api/financials-client/financials-client.type';
import { useFinancialsStore } from '../../../store/financials-store/financials-store';
import { Charts as ChartsType } from '../../../store/financials-store/financials-store.type';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { useAgentCosts, useAllocationOfCosts, useCostByInitiative, useCostVersusOutput } from '../financials.hooks';

interface ChartDataProviderProps {
  children: (data: {
    agentCostsData: AgentFinancialResponse;
    allocationOfCostsData: AllocationOfCostsChartResponseByCategory;
    costByInitiativeData: CostByInitiativeResponse[];
    costVersusOutputData: CostVsBudgetChartResponse;
    isPending: boolean;
    isError: boolean;
  }) => ReactNode;
}

export function ChartDataProvider({ children }: ChartDataProviderProps) {
  const selectedChart = useFinancialsStore((state) => state.chart);
  const portfolio = useGlobalStore((state) => state.portfolio);
  const teamId = useFinancialsStore((state) => state.team);
  const boardId = useFinancialsStore((state) => state.board);
  const startDate = useFinancialsStore((state) => state.startDate);
  const endDate = useFinancialsStore((state) => state.endDate);

  // Common parameters for all hooks
  const startDateValue = dayjs(startDate).toDate();
  const endDateValue = dayjs(endDate).toDate();
  const portfolioId = portfolio?.id || null;

  // Only fetch data for the currently selected chart
  const isAgentCosts = selectedChart === ChartsType.AgentCosts;
  const isAllocationOfCosts = selectedChart === ChartsType.AllocationOfCosts;
  const isCostByInitiative = selectedChart === ChartsType.CostByInitiative;
  const isCostVersusOutput = selectedChart === ChartsType.CostVersusOutput;

  // Fetch data for AgentCosts only when selected
  const { data: agentCostsData, query: agentCostsQuery } = useAgentCosts(
    startDateValue,
    endDateValue,
    portfolioId,
    isAgentCosts,
  );

  // Fetch data for AllocationOfCosts only when selected
  const { data: allocationOfCostsData, query: allocationOfCostsQuery } = useAllocationOfCosts(
    startDateValue,
    endDateValue,
    portfolioId,
    teamId,
    boardId,
    isAllocationOfCosts,
  );

  // Fetch data for CostByInitiative only when selected
  const { data: costByInitiativeData, query: costByInitiativeQuery } = useCostByInitiative(
    startDateValue,
    endDateValue,
    portfolioId,
    teamId,
    boardId,
    isCostByInitiative,
  );

  // Fetch data for CostVersusOutput only when selected
  const { data: costVersusOutputData, query: costVersusOutputQuery } = useCostVersusOutput(
    startDateValue,
    endDateValue,
    portfolioId,
    teamId,
    boardId,
    isCostVersusOutput,
  );

  // Determine which loading state to use based on selected chart
  let isPending = false;
  let isError = false;

  switch (selectedChart) {
    case ChartsType.AgentCosts:
      isPending = agentCostsQuery.isPending;
      isError = agentCostsQuery.isError;
      break;
    case ChartsType.AllocationOfCosts:
      isPending = allocationOfCostsQuery.isPending;
      isError = allocationOfCostsQuery.isError;
      break;
    case ChartsType.CostByInitiative:
      isPending = costByInitiativeQuery.isPending;
      isError = costByInitiativeQuery.isError;
      break;
    case ChartsType.CostVersusOutput:
      isPending = costVersusOutputQuery.isPending;
      isError = costVersusOutputQuery.isError;
      break;
  }

  return (
    <>
      {children({
        agentCostsData:
          agentCostsData ||
          ({
            dates: [],
            agent_sources: [],
            tasks: [],
            users: [],
            epics: [],
            initiatives: [],
            summary: { total_cost: 0, total_tokens: 0, total_sessions: 0, active_users: 0, median_cost_per_session: 0 },
          } as AgentFinancialResponse),
        allocationOfCostsData: allocationOfCostsData || ({} as AllocationOfCostsChartResponseByCategory),
        costByInitiativeData: costByInitiativeData || ([] as CostByInitiativeResponse[]),
        costVersusOutputData: costVersusOutputData || ({} as CostVsBudgetChartResponse),
        isPending,
        isError,
      })}
    </>
  );
}
